'use client';

import type { CSSProperties, ReactElement, ReactNode, RefObject } from 'react';
import {
  AnimatePresence,
  motion,
  type TargetAndTransition,
  type Transition,
} from 'framer-motion';
import { dataEl, type DataElScope } from '@/utils/dataEl';
import {
  MESSAGE_MODAL_BACKDROP_FADE_DURATION_SECONDS,
  MESSAGE_MODAL_ENTRANCE_DURATION_SECONDS,
  MESSAGE_MODAL_EXIT_DURATION_SECONDS,
} from './messageModalConstants';

const BACKDROP_TRANSITION: Transition = {
  duration: MESSAGE_MODAL_BACKDROP_FADE_DURATION_SECONDS,
  ease: [0.4, 0, 0.2, 1],
};

const ENTRANCE_TRANSITION: Transition = {
  duration: MESSAGE_MODAL_ENTRANCE_DURATION_SECONDS,
  ease: [0.22, 1, 0.36, 1],
  delay: MESSAGE_MODAL_BACKDROP_FADE_DURATION_SECONDS,
};

const EXIT_TRANSITION: Transition = {
  duration: MESSAGE_MODAL_EXIT_DURATION_SECONDS,
  ease: [0.4, 0, 1, 1],
};

const REDUCED_MOTION_TRANSITION: Transition = { duration: 0 };
const VISIBLE_MOTION_STATE: TargetAndTransition = {
  opacity: 1,
  scale: 1,
  filter: 'blur(0px)',
};
const ENTRANCE_MOTION_STATE: TargetAndTransition = {
  opacity: 0,
  scale: 0.86,
  filter: 'blur(18px)',
};
const EXIT_MOTION_STATE: TargetAndTransition = {
  opacity: 0,
  scale: 0.94,
  filter: 'blur(14px)',
  transition: EXIT_TRANSITION,
};
const REDUCED_MOTION_EXIT_STATE: TargetAndTransition = {
  opacity: 0,
  transition: REDUCED_MOTION_TRANSITION,
};

interface MessageModalFrameProps {
  dataElScope: DataElScope;
  visible: boolean;
  loaded: boolean;
  reduceMotion: boolean;
  rootRef: RefObject<HTMLDivElement | null>;
  rootStyle: CSSProperties;
  containerStyle: CSSProperties;
  onBackdropClick: () => void;
  children: ReactNode;
}

export function MessageModalFrame({
  dataElScope,
  visible,
  loaded,
  reduceMotion,
  rootRef,
  rootStyle,
  containerStyle,
  onBackdropClick,
  children,
}: MessageModalFrameProps): ReactElement {

  return (
      <AnimatePresence>
        {visible ? (
          <motion.div
            data-el={dataEl(dataElScope, 'Frame/Backdrop')}
            ref={rootRef}
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={reduceMotion ? REDUCED_MOTION_TRANSITION : BACKDROP_TRANSITION}
            style={rootStyle}
            onClick={onBackdropClick}
          >
            {loaded ? (
              <motion.div
                data-el={dataEl(dataElScope, 'Frame/Panel')}
                initial={reduceMotion ? false : ENTRANCE_MOTION_STATE}
                animate={VISIBLE_MOTION_STATE}
                exit={reduceMotion ? REDUCED_MOTION_EXIT_STATE : EXIT_MOTION_STATE}
                transition={reduceMotion ? REDUCED_MOTION_TRANSITION : ENTRANCE_TRANSITION}
                style={containerStyle}
                onClick={(event) => event.stopPropagation()}
              >
                {children}
              </motion.div>
            ) : null}
          </motion.div>
        ) : null}
      </AnimatePresence>
  );
}
